import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Tabs,
  TabList,
  Tab,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  useToast,
  useBreakpointValue,
  Select,
  IconButton,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Checkbox,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  MagnifyingGlass,
  CheckCircle,
  Hourglass,
  Clock,
  User,
  Funnel,
  List,
} from '@phosphor-icons/react'
import { formatDistanceToNow, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import api from '../services/api'
import socketService, { SOCKET_EVENTS } from '../services/socket'
import Chat from './Tickets/Chat'
import UserInfoSidebar from './Tickets/UserInfoSidebar'
import { WhatsappBadge, formatWaId } from '../components/WhatsappBadge'
import { GuestBadge } from './Tickets/GuestInfoPanel'
import { Sidebar } from '../layouts/DefaultLayout'
import AccessTags, { type AccessTag } from '../components/AccessTags'

interface SupportChat {
  id: number
  status: string
  userId: number
  ownerName: string
  ownerEmail: string
  ownerProfilePic: string | null
  /** Planos e acessos do vendedor (catálogo do admin). */
  ownerTags?: AccessTag[]
  agentName: string | null
  agentEmail: string | null
  categoryName: string
  categoryColor: string | null
  customStatusName: string | null
  customStatusColor: string | null
  unreadCountAgent: number
  createdAt: string
  updatedAt: string
  lastMessage?: {
    message: string
    createdAt: string
    senderType: string
  }
  /** Visitante da tela de login (support_chats.guestSessionId). O dono é um usuário-sombra. */
  isGuest?: boolean
  guestEmail?: string | null
  guestName?: string | null
  channel?: 'web' | 'whatsapp'
  whatsappWaId?: string | null
  whatsappProfileName?: string | null
  accountStatus?: string | null
  diagnosisCode?: string | null
  guestLinkedUserId?: number | null
}

interface Category {
  id: number
  name: string
  color: string
}

const STATUS_MAP = ['pending', 'in_progress', 'completed'] as const

function ChatItem({ chat, isSelected, onClick }: {
  chat: SupportChat
  isSelected: boolean
  onClick: () => void
}) {
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  return (
    <Box
      p={3}
      borderRadius="lg"
      bg={isSelected ? subtleBg : 'transparent'}
      _hover={{ bg: isSelected ? subtleBg : hoverBg }}
      cursor="pointer"
      onClick={onClick}
      borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
      borderLeftColor={isSelected ? 'yellow.500' : 'transparent'}
      borderBottom="1px solid"
      borderBottomColor={borderColor}
      flexShrink={0}
      transition="background-color 0.2s ease"
    >
      <HStack spacing={3} align="start">
        <Avatar
          size="sm"
          name={chat.ownerName}
          src={chat.ownerProfilePic || undefined}
          bg="yellow.500"
          color="gray.900"
          flexShrink={0}
        />
        <VStack flex={1} align="start" spacing={0} overflow="hidden" minW={0}>
          <HStack w="100%" justify="space-between" align="start">
            <Text fontWeight="semibold" color={textPrimary} noOfLines={1} fontSize="sm">
              {chat.ownerName}
            </Text>
            {chat.unreadCountAgent > 0 && (
              <Badge colorScheme="red" borderRadius="full" fontSize="10px" minW={4} textAlign="center">
                {chat.unreadCountAgent > 99 ? '99+' : chat.unreadCountAgent}
              </Badge>
            )}
          </HStack>
          <Text fontSize="xs" color={textMuted} noOfLines={1}>
            {chat.channel === 'whatsapp' && chat.whatsappWaId ? formatWaId(chat.whatsappWaId) : chat.isGuest && !chat.guestLinkedUserId && chat.guestEmail ? chat.guestEmail : chat.ownerEmail}
          </Text>
          <HStack spacing={1} flexWrap="wrap" mt={0.5}>
            <Badge
              size="sm"
              bg={chat.categoryColor || 'blue.500'}
              color="white"
              fontSize="10px"
              borderRadius="full"
              px={1}
            >
              {chat.categoryName || 'Sem categoria'}
            </Badge>
            {chat.isGuest && chat.channel !== 'whatsapp' && <GuestBadge size="xs" />}
            {chat.channel === 'whatsapp' && <WhatsappBadge size="xs" />}
            {chat.customStatusName && (
              <Badge
                size="sm"
                bg={chat.customStatusColor || 'gray.600'}
                color="white"
                fontSize="10px"
                borderRadius="full"
                px={1}
              >
                {chat.customStatusName}
              </Badge>
            )}
          </HStack>
          {chat.ownerTags && chat.ownerTags.length > 0 && (
            <Box mt={1} w="100%">
              <AccessTags tags={chat.ownerTags} size="xs" max={3} />
            </Box>
          )}
          {chat.agentName && (
            <HStack spacing={1} mt={0.5}>
              <User size={10} color="#A0AEC0" />
              <Text fontSize="xs" color={textMuted} noOfLines={1}>
                {chat.agentName}
              </Text>
            </HStack>
          )}
          <Text fontSize="xs" color={textMuted} noOfLines={1} mt={1}>
            {chat.lastMessage?.message || 'Sem mensagens'}
          </Text>
          <HStack w="100%" justify="space-between" mt={1}>
            <Text fontSize="2xs" color={textMuted}>
              Aberto: {format(new Date(chat.createdAt), 'dd/MM/yy HH:mm', { locale: ptBR })}
            </Text>
            <Text fontSize="2xs" color={textMuted} flexShrink={0} ml={2}>
              Última msg: {formatDistanceToNow(new Date(chat.updatedAt), { addSuffix: true, locale: ptBR })}
            </Text>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  )
}

export default function AllChats() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')

  const [activeTab, setActiveTab] = useState(0)
  const [chats, setChats] = useState<SupportChat[]>([])
  const chatsRef = useRef<SupportChat[]>([])
  chatsRef.current = chats
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedPlan, setSelectedPlan] = useState<string>('')
  const [categories, setCategories] = useState<Category[]>([])
  // No celular o painel do cliente é drawer: começa fechado.
  const [showUserInfo, setShowUserInfo] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 62em)').matches,
  )
  const [counts, setCounts] = useState({ pending: 0, in_progress: 0, completed: 0 })
  const [tempCategory, setTempCategory] = useState<string>('')
  const [tempPlan, setTempPlan] = useState<string>('')
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure()
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()
  
  const menuIconColor = useColorModeValue('gray.700', 'white')

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selectedChatId = id ? Number(id) : null

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/categories')
      setCategories(res.data.data || res.data)
    } catch {
      // ignore
    }
  }, [])

  const fetchInternalTicketsCount = useCallback(async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }, [])

  const fetchChats = useCallback(async (status: string, pageNum: number, searchTerm: string, categoryId: string, planType: string, append: boolean) => {
    setIsLoading(true)
    try {
      const params: any = { status, page: pageNum, limit: 30 }
      if (searchTerm) params.search = searchTerm
      if (categoryId) params.categoryId = categoryId
      if (planType) params.planType = planType

      const res = await api.get('/chats', { params })
      const data = res.data.data || []
      const meta = res.data.meta || {}

      if (append) {
        setChats(prev => [...prev, ...data])
      } else {
        setChats(data)
      }
      setHasMore(pageNum < (meta.lastPage || 1))
    } catch {
      toast({ title: 'Erro ao carregar conversas', status: 'error', duration: 3000 })
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  const fetchCounts = useCallback(async () => {
    try {
      const [pendingRes, progressRes, completedRes] = await Promise.all([
        api.get('/chats', { params: { status: 'pending', page: 1, limit: 1 } }),
        api.get('/chats', { params: { status: 'in_progress', page: 1, limit: 1 } }),
        api.get('/chats', { params: { status: 'completed', page: 1, limit: 1 } }),
      ])
      setCounts({
        pending: pendingRes.data.meta?.total || 0,
        in_progress: progressRes.data.meta?.total || 0,
        completed: completedRes.data.meta?.total || 0,
      })
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    fetchCategories()
    fetchCounts()
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [fetchCategories, fetchCounts, fetchInternalTicketsCount])

  useEffect(() => {
    setPage(1)
    setChats([])
    setHasMore(true)
    fetchChats(STATUS_MAP[activeTab], 1, search, selectedCategory, selectedPlan, false)
  }, [activeTab, search, selectedCategory, selectedPlan, fetchChats])

  // Lista viva. Antes ela só recarregava ao trocar aba/filtro: um chat assumido
  // por outro atendente continuava em "Pendentes" com "última msg há 2 horas"
  // até alguém mexer no filtro. Agora qualquer evento de chat recarrega a aba
  // atual com debounce (rajadas viram um fetch), e os contadores junto.
  const filtrosRef = useRef({ activeTab, search, selectedCategory, selectedPlan })
  useEffect(() => {
    filtrosRef.current = { activeTab, search, selectedCategory, selectedPlan }
  }, [activeTab, search, selectedCategory, selectedPlan])

  useEffect(() => {
    let listaTimer: ReturnType<typeof setTimeout> | null = null
    let contadoresTimer: ReturnType<typeof setTimeout> | null = null

    const recarregarLista = () => {
      if (listaTimer) clearTimeout(listaTimer)
      listaTimer = setTimeout(() => {
        const f = filtrosRef.current
        setPage(1)
        fetchChats(STATUS_MAP[f.activeTab], 1, f.search, f.selectedCategory, f.selectedPlan, false)
      }, 3000)
    }
    const recarregarContadores = () => {
      if (contadoresTimer) clearTimeout(contadoresTimer)
      contadoresTimer = setTimeout(() => fetchCounts(), 4000)
    }
    const mudancaDeEstado = () => {
      recarregarLista()
      recarregarContadores()
    }

    const eventosDeEstado = [
      SOCKET_EVENTS.CHAT.NEW,
      SOCKET_EVENTS.CHAT.STARTED,
      SOCKET_EVENTS.CHAT.FINISHED,
      SOCKET_EVENTS.CHAT.TRANSFERRED,
      SOCKET_EVENTS.CHAT.UPDATED,
    ]
    // Como no WhatsApp: mensagem nova sobe a conversa para o topo NA HORA, com
    // a prévia atualizada. Chat que não está na lista (outra aba/filtro/página)
    // fica para o recarregamento com debounce.
    const atividade = (data: { chatId: number; lastMessage: SupportChat['lastMessage'] }) => {
      const chatId = Number(data?.chatId)
      if (!chatId) return
      if (!chatsRef.current.some((c) => Number(c.id) === chatId)) {
        recarregarLista()
        return
      }
      setChats((prev) => {
        const idx = prev.findIndex((c) => Number(c.id) === chatId)
        if (idx === -1) return prev
        const atual = prev[idx]
        const atualizado: SupportChat = {
          ...atual,
          lastMessage: data.lastMessage ?? atual.lastMessage,
          updatedAt: data.lastMessage?.createdAt ?? new Date().toISOString(),
        }
        return [atualizado, ...prev.slice(0, idx), ...prev.slice(idx + 1)]
      })
    }

    eventosDeEstado.forEach((evento) => socketService.on(evento, mudancaDeEstado))
    socketService.on(SOCKET_EVENTS.CHAT.ACTIVITY, atividade)

    return () => {
      eventosDeEstado.forEach((evento) => socketService.off(evento, mudancaDeEstado))
      socketService.off(SOCKET_EVENTS.CHAT.ACTIVITY, atividade)
      if (listaTimer) clearTimeout(listaTimer)
      if (contadoresTimer) clearTimeout(contadoresTimer)
    }
  }, [fetchChats, fetchCounts])

  const handleSearchChange = (value: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value)
    }, 400)
  }

  const handleScroll = () => {
    if (!listRef.current || isLoading || !hasMore) return
    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 100) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchChats(STATUS_MAP[activeTab], nextPage, search, selectedCategory, selectedPlan, true)
    }
  }

  const handleTabChange = (index: number) => {
    setActiveTab(index)
  }

  const handleChatClick = (chatId: number) => {
    navigate(`/all-chats/${chatId}`)
  }

  const handleChatFinished = () => {
    fetchChats(STATUS_MAP[activeTab], 1, search, selectedCategory, selectedPlan, false)
    fetchCounts()
  }

  const planLabels: Record<string, string> = {
    default: 'Plano Padrão',
    isLightShopee: 'Light Shopee',
    isMariaMonteiro: 'Maria Monteiro',
    isGuilhermeCirilo: 'Guilherme Cirilo',
    isElite: 'Elite',
    isGold: 'Gold',
    isMembroBlack: 'Membro Black',
  }

  const openFilterModal = () => {
    setTempCategory(selectedCategory)
    setTempPlan(selectedPlan)
    onFilterOpen()
  }

  const applyFilters = () => {
    setSelectedCategory(tempCategory)
    setSelectedPlan(tempPlan)
    onFilterClose()
  }

  const activeFilterCount = (selectedCategory ? 1 : 0) + (selectedPlan ? 1 : 0)
  const hasActiveFilters = activeFilterCount > 0

  if (isMobile && selectedChatId) {
    return (
      <Flex h="100%" direction="column">
        {/* Celular: conversa em tela cheia; o voltar fica no cabeçalho do Chat */}
        <Box flex={1} minH={0} overflow="hidden">
          <Chat
            chatId={selectedChatId}
            onToggleUserInfo={() => setShowUserInfo(!showUserInfo)}
            onChatFinished={handleChatFinished}
            onClose={() => navigate('/all-chats')}
          />
        </Box>
        {showUserInfo && (() => {
          const current = chats.find((c) => c.id === selectedChatId)
          return (
            <UserInfoSidebar
              chatId={selectedChatId}
              userId={current?.userId || 0}
              isGuest={!!current?.isGuest}
              guestLinkedUserId={current?.guestLinkedUserId ?? null}
              onClose={() => setShowUserInfo(false)}
              isOpen={showUserInfo}
            />
          )
        })()}
      </Flex>
    )
  }

  return (
    <>
      {/* Mobile menu drawer */}
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Flex h="100%" overflow="hidden">
        {/* Sidebar - lista de chats */}
      <Box
        w={selectedChatId ? '380px' : '100%'}
        maxW={selectedChatId ? '380px' : undefined}
        borderRight={selectedChatId ? '1px' : undefined}
        borderColor={borderColor}
        h="100%"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <VStack p={4} spacing={3} borderBottom="1px" borderColor={borderColor} flexShrink={0}>
          <HStack spacing={3} align="center" w="100%">
            {isMobile && (
              <IconButton
                aria-label="Menu"
                icon={<List size={20} />}
                variant="ghost"
                onClick={onMenuOpen}
                size="sm"
                color={menuIconColor}
                _hover={{ bg: borderColor }}
                flexShrink={0}
              />
            )}
            <Text fontSize="lg" fontWeight="bold" color={textPrimary}>
              Todas as Conversas
            </Text>
          </HStack>
          <HStack w="100%" spacing={2}>
            <InputGroup size="sm" flex={1}>
              <InputLeftElement>
                <MagnifyingGlass size={16} color="#A0AEC0" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por nome, email ou ID..."
                onChange={(e) => handleSearchChange(e.target.value)}
                bg={surfaceBg}
                border="1px solid"
                borderColor={borderSubtle}
              />
            </InputGroup>
            <Button
              size="sm"
              variant="outline"
              colorScheme={hasActiveFilters ? 'yellow' : 'gray'}
              leftIcon={<Funnel size={14} />}
              onClick={openFilterModal}
              flexShrink={0}
            >
              Filtrar
              {hasActiveFilters && (
                <Badge
                  ml={1}
                  bg="yellow.500"
                  color="gray.900"
                  borderRadius="full"
                  fontSize="10px"
                  minW="18px"
                  textAlign="center"
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </HStack>
          {hasActiveFilters && (
            <Wrap spacing={1} w="100%">
              {selectedPlan && (
                <WrapItem>
                  <Tag size="sm" colorScheme="yellow" borderRadius="full" variant="subtle">
                    <TagLabel>{planLabels[selectedPlan] || selectedPlan}</TagLabel>
                    <TagCloseButton onClick={() => setSelectedPlan('')} />
                  </Tag>
                </WrapItem>
              )}
              {selectedCategory && (
                <WrapItem>
                  <Tag size="sm" colorScheme="gray" borderRadius="full" variant="subtle">
                    <TagLabel>{categories.find((c) => String(c.id) === selectedCategory)?.name || 'Categoria'}</TagLabel>
                    <TagCloseButton onClick={() => setSelectedCategory('')} />
                  </Tag>
                </WrapItem>
              )}
              <WrapItem>
                <Button
                  size="xs"
                  variant="ghost"
                  color={textMuted}
                  _hover={{ color: textPrimary }}
                  onClick={() => { setSelectedCategory(''); setSelectedPlan('') }}
                  fontSize="xs"
                >
                  Limpar
                </Button>
              </WrapItem>
            </Wrap>
          )}
          <Tabs
            index={activeTab}
            onChange={handleTabChange}
            variant="soft-rounded"
            colorScheme="yellow"
            size="sm"
            w="100%"
          >
            <TabList>
              <Tab flex={1} fontSize="xs">
                <HStack spacing={1}>
                  <Clock size={14} />
                  <Text>Pendentes</Text>
                  {counts.pending > 0 && (
                    <Badge colorScheme="orange" borderRadius="full" fontSize="10px">
                      {counts.pending}
                    </Badge>
                  )}
                </HStack>
              </Tab>
              <Tab flex={1} fontSize="xs">
                <HStack spacing={1}>
                  <Hourglass size={14} />
                  <Text>Andamento</Text>
                  {counts.in_progress > 0 && (
                    <Badge colorScheme="blue" borderRadius="full" fontSize="10px">
                      {counts.in_progress}
                    </Badge>
                  )}
                </HStack>
              </Tab>
              <Tab flex={1} fontSize="xs">
                <HStack spacing={1}>
                  <CheckCircle size={14} />
                  <Text>Concluídos</Text>
                </HStack>
              </Tab>
            </TabList>
          </Tabs>
        </VStack>

        <Modal isOpen={isFilterOpen} onClose={onFilterClose} size="sm" isCentered>
          <ModalOverlay />
          <ModalContent bg={surfaceBg} borderColor={borderColor} borderWidth="1px">
            <ModalHeader fontSize="md" pb={2}>Filtrar Conversas</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2} color={textSecondary}>
                    Plano
                  </Text>
                  <Select
                    size="sm"
                    value={tempPlan}
                    onChange={(e) => setTempPlan(e.target.value)}
                    bg={subtleBg}
                    borderColor={borderSubtle}
                  >
                    <option value="">Todos os Planos</option>
                    <option value="default">Plano Padrão</option>
                    <option value="isLightShopee">Light Shopee</option>
                    <option value="isMariaMonteiro">Maria Monteiro</option>
                    <option value="isGuilhermeCirilo">Guilherme Cirilo</option>
                    <option value="isElite">Elite</option>
                    <option value="isGold">Gold</option>
                    <option value="isMembroBlack">Membro Black</option>
                  </Select>
                </Box>

                {categories.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2} color={textSecondary}>
                      Categoria
                    </Text>
                    <SimpleGrid columns={1} spacing={1}>
                      {categories.map((cat) => (
                        <Checkbox
                          key={cat.id}
                          size="sm"
                          colorScheme="yellow"
                          isChecked={tempCategory === String(cat.id)}
                          onChange={(e) => {
                            setTempCategory(e.target.checked ? String(cat.id) : '')
                          }}
                        >
                          <Text fontSize="sm">{cat.name}</Text>
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter pt={2}>
              <Button size="sm" variant="ghost" mr={2} onClick={onFilterClose}>
                Cancelar
              </Button>
              <Button
                size="sm"
                bg="yellow.500"
                color="gray.900"
                _hover={{ bg: 'yellow.400' }}
                onClick={applyFilters}
              >
                Aplicar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Chat list */}
        <Box
          ref={listRef}
          flex={1}
          minH={0}
          overflowY="auto"
          onScroll={handleScroll}
        >
          {chats.length === 0 && !isLoading ? (
            <Flex h="200px" align="center" justify="center">
              <Text color={textMuted} fontSize="sm">
                Nenhuma conversa encontrada
              </Text>
            </Flex>
          ) : (
            chats.map((chat) => (
              <ChatItem
                key={chat.id}
                chat={chat}
                isSelected={selectedChatId === chat.id}
                onClick={() => handleChatClick(chat.id)}
              />
            ))
          )}
          {isLoading && (
            <Flex p={4} justify="center">
              <Spinner size="sm" color="yellow.500" />
            </Flex>
          )}
        </Box>
      </Box>

      {/* Chat area */}
      {selectedChatId ? (
        <>
          <Box flex={1} h="100%">
            <Chat
              chatId={selectedChatId}
              onToggleUserInfo={() => setShowUserInfo(!showUserInfo)}
              onChatFinished={handleChatFinished}
              onClose={() => navigate('/all-chats')}
            />
          </Box>
          {showUserInfo && (() => {
            const current = chats.find((c) => c.id === selectedChatId)
            return (
              <Box w="320px" h="100%" borderLeft="1px" borderColor={borderColor} overflowY="auto">
                <UserInfoSidebar
                  chatId={selectedChatId}
                  userId={current?.userId || 0}
                  isGuest={!!current?.isGuest}
                  guestLinkedUserId={current?.guestLinkedUserId ?? null}
                  onGuestLinked={(linkedId) =>
                    setChats((prev) =>
                      prev.some((c) => c.id === selectedChatId && c.guestLinkedUserId !== linkedId)
                        ? prev.map((c) =>
                            c.id === selectedChatId ? { ...c, userId: linkedId, guestLinkedUserId: linkedId } : c
                          )
                        : prev
                    )
                  }
                  onClose={() => setShowUserInfo(false)}
                />
              </Box>
            )
          })()}
        </>
      ) : null}
    </Flex>
    </>
  )
}
